import { useMutation, useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo } from "react";

import { ApiError } from "../../lib/api";
import { useStudioSettings } from "../../stores/studio";
import { banners } from "../profile/banner";
import { rooms, type Room } from "../rooms/api";
import { isListeningTo, startListening } from "../rooms/listening";
import { useRoom } from "../rooms/useRoom";
import { setMonitor } from "./audio";
import { studio } from "./api";
import type { Scene } from "./compositor";
import { CANVAS_HEIGHT, CANVAS_WIDTH } from "./layouts";
import { activeScene } from "./scene";
import { useStudio } from "./useStudio";

/**
 * The studio's engine, mounted in the app shell.
 *
 * ## Why it is here and not in the console
 *
 * It used to live inside the console's studio column, which made a broadcast a
 * property of a route: navigating to a profile, a track or the inbox unmounted
 * the component, and unmounting stops the camera, tears down the peer
 * connection and ends the show. A host who clicked a notification lost their
 * set, with no warning and no way back except starting again.
 *
 * So the engine sits above the router, the way `PlayerHost` already does for
 * audio, and the console draws controls onto it. The same reasoning, the same
 * shape: the thing with a lifetime lives where lifetimes are long.
 *
 * ## The output canvas is here; the preview is not
 *
 * `captureStream()` needs a canvas that is in the document, so the 720p canvas
 * being encoded is rendered here, hidden, for as long as the studio is open.
 * The preview the host looks at belongs to the console and attaches itself
 * through this context - so navigating away stops the *preview* and nothing
 * else, which is exactly right.
 *
 * ## It costs nothing when nobody is broadcasting
 *
 * Everything is gated on `open`, which only the console sets. Until then there
 * is no room query, no render loop and no canvas - just an empty component in
 * everybody's shell.
 */

type StudioValue = ReturnType<typeof useStudioEngine>;

const StudioContext = createContext<StudioValue | null>(null);

export function useStudioSession(): StudioValue {
  const value = useContext(StudioContext);

  if (!value)
    throw new Error("Studio controls must be rendered inside StudioSession.");

  return value;
}

function useStudioEngine() {
  const open = useStudioSettings((s) => s.open);
  const settings = useStudioSettings();

  /*
   * The host's own room, and only while the studio is open.
   *
   * This component is in every signed-in user's shell, so an unconditional
   * query here would poll `/me/station` from every page for people who have
   * never hosted anything.
   */
  const view = useRoom(undefined, { mine: true, enabled: open });
  const room: Room | null = view.data?.room ?? null;

  const presets = useQuery({
    queryKey: ["banner-presets"],
    queryFn: banners.presets,
    enabled: open,
  });

  const credentials = useQuery({
    queryKey: ["studio-credentials"],
    queryFn: studio.credentials,
    enabled: open,
    // The key is write access to this broadcast. Nothing about it should sit in
    // a cache waiting for a shoulder to look over.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  /*
   * The arrangement, from the host's active scene.
   *
   * Read out here rather than inside the memo so the dependency is the scene
   * itself: the store replaces it on every edit, so a changed layer is a
   * changed reference and the frame repaints. Depending on `book` would work
   * too and would repaint on edits to scenes nobody is looking at.
   */
  const scene = activeScene(settings.book);

  const composed: Scene = useMemo(
    () => ({
      layers: scene.layers,
      background: scene.background?.stops ?? ["#0d0d10", "#1a1a20"],
      backgroundAngle: scene.background?.angle ?? 160,
      camera: null,
      track: room?.now_playing
        ? {
            title: room.now_playing.title,
            artist: room.now_playing.artist.display_name,
            artwork: room.now_playing.artwork ?? room.now_playing.artwork_small,
          }
        : null,
      brand: room
        ? {
            username: room.host.username,
            displayName: room.host.display_name,
            avatar: room.host.avatar,
          }
        : null,
      live: room?.status === "live",
    }),
    [scene, room],
  );

  const deck = useStudio(composed, open);
  const broadcasting = deck.status === "live" || deck.status === "reconnecting";

  // The mix does not exist until the broadcast starts, so the levels are
  // applied on change *and* re-applied here - otherwise a host who set them
  // before going live would find them ignored.
  useEffect(
    () => deck.setDeckLevel(settings.deckLevel),
    [deck, settings.deckLevel],
  );
  useEffect(
    () => deck.setMicLevel(settings.micLevel),
    [deck, settings.micLevel],
  );

  /*
   * The host always hears the deck.
   *
   * This was a setting, and being able to switch it off was the problem: the
   * deck feeds the outgoing mix, so a host who had muted their own monitor
   * could not tell a broadcast carrying music from one carrying silence. The
   * only way to hear that problem is to be listening to it.
   *
   * Re-applied when a broadcast starts rather than set once, because the gain
   * node does not exist until the deck is tapped and the deck is tapped when
   * the broadcast begins.
   *
   * A host who wants it quieter has the dock's volume, which is the ordinary
   * control for that and does not reach the broadcast.
   */
  useEffect(() => setMonitor(true), [deck.status]);

  /*
   * While broadcasting, the host is in their own room whether they like it or
   * not.
   *
   * `RoomAudio` - which is what actually loads and plays the track - only
   * exists while somebody is tuned. So a host who had not pressed Monitor was
   * broadcasting a mix with no music in it, and could not tell, because the
   * only way to hear the problem is to be monitoring.
   *
   * Tuning is therefore not the host's choice during a broadcast; whether they
   * *hear* it is, and that is the Monitor toggle in the panel.
   */
  useEffect(() => {
    if (!broadcasting || !room) return;
    if (!isListeningTo(room.username)) startListening(room);
  }, [broadcasting, room]);

  /*
   * Publish as soon as the room is in studio mode, without being asked again.
   *
   * The header's "Go on air" is the whole action: it opens the room *and* puts
   * it in studio mode. Making the host then find a second button inside the
   * panel and press go-live again was the confusion that produced this - two
   * controls for one intention, in two places, with different words.
   *
   * `idle` only, so this cannot fight anything: a broadcast that is already
   * publishing is not `idle`, and one that failed sits in `error` until the
   * host retries rather than being restarted in a loop.
   *
   * The user gesture browsers require for audio has already happened - it was
   * the click on "Go on air" - and activation is sticky for the page, so
   * resuming the AudioContext from here is allowed.
   */
  useEffect(() => {
    if (!open || deck.status !== "idle") return;
    if (!credentials.data) return;
    if (room?.status !== "live" || room.source !== "stream") return;

    void deck.start(credentials.data.whip);
  }, [open, deck, credentials.data, room?.status, room?.source]);

  /*
   * And stop when the room does, which had no counterpart at all.
   *
   * Going on air starts the studio publishing; ending the broadcast did not
   * stop it. The room went offline and the engine carried on holding a WHIP
   * session open, which showed up as the panel still reading "connected" beside
   * a room that was plainly off air - reported as confusing, and the confusion
   * was accurate: it *was* still streaming.
   *
   * Worse than the label. An abandoned publisher keeps Cloudflare's live input
   * busy, and Cloudflare refuses a second publish to a busy input with a 409
   * for about half a minute - so the next broadcast started by failing, for
   * reasons that pointed nowhere near here.
   *
   * Guarded on `room` being present, not merely falsy: the query behind it is
   * disabled while the studio is closed, and "I do not know what the room is
   * doing" must not read as "the room is off air".
   */
  useEffect(() => {
    if (!broadcasting || !room) return;
    if (room.status === "live" && room.source === "stream") return;

    void deck.stop();
  }, [broadcasting, room, deck]);

  /*
   * A reload or a closed tab still ends the broadcast, so it takes a
   * deliberate click.
   *
   * Navigating *within* the app no longer needs this - that is the whole point
   * of mounting the engine up here - but a browser reload tears down the page
   * and nothing can survive it.
   *
   * Browsers ignore the message and show their own wording; the value is the
   * prompt existing at all.
   */
  useEffect(() => {
    if (!broadcasting) return;

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Assigned as well as prevented: older browsers need the value set, and
      // it costs nothing on the ones that do not.
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);

    return () => window.removeEventListener("beforeunload", warn);
  }, [broadcasting]);

  return useMemo(
    () => ({
      deck,
      room,
      broadcasting,
      presets: presets.data?.presets ?? [],
      credentials: credentials.data ?? null,
      /** True when the platform has the studio switched off. */
      missing:
        credentials.error instanceof ApiError &&
        credentials.error.status === 404,
      refreshRoom: () => void view.refetch(),
    }),
    [
      deck,
      room,
      broadcasting,
      presets.data,
      credentials.data,
      credentials.error,
      view,
    ],
  );
}

export function StudioSession({ children }: { children: React.ReactNode }) {
  const value = useStudioEngine();
  const open = useStudioSettings((s) => s.open);

  return (
    <StudioContext.Provider value={value}>
      {children}

      {/*
        The canvas being encoded. Hidden rather than unmounted, because
        `captureStream` needs a canvas that is in the document and an unmounted
        one produces a stream of nothing.
      */}
      {open && (
        <canvas
          ref={value.deck.outputRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="hidden"
          aria-hidden
          data-studio-output
        />
      )}
    </StudioContext.Provider>
  );
}

/** Going on air in studio mode, which the panel triggers and the engine needs. */
export function useGoLiveInStudio(onDone: () => void) {
  return useMutation({
    // `'stream'` on the way in, so the room is in the mode the ingest server
    // requires before the host can press start.
    mutationFn: () => rooms.goLive("stream"),
    onSuccess: onDone,
  });
}
